import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { ListNotificationsInput } from '@erp/rpc';

import { orpc } from '@/api/rpc/client';

/**
 * Polling cadence for the bell's unread count while the user is active.
 * Pauses automatically when the tab is hidden (refetchIntervalInBackground)
 * or when the caller-provided `isIdle` flag is true.
 */
const UNREAD_POLL_INTERVAL_MS = 30_000;

export function useUnreadNotificationsCount({
  isIdle = false,
}: { isIdle?: boolean } = {}) {
  return useQuery({
    ...orpc.communications.notifications.unreadCount.queryOptions(),
    refetchInterval: isIdle ? false : UNREAD_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * Sender options shown in the `send_announcement` step config: one
 * SendGrid default plus any enabled Microsoft Graph mailboxes for the
 * current org. Org-scoped on the backend.
 */
export function useNotificationSenderOptions() {
  return useQuery({
    ...orpc.communications.notifications.listSenderOptions.queryOptions(),
  });
}

export function useNotificationsList(
  params?: ListNotificationsInput,
  enabled = true,
) {
  return useQuery({
    ...orpc.communications.notifications.list.queryOptions({
      input: params ?? { limit: 20, unreadOnly: false, filter: 'active' },
    }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * All notification mutations invalidate both `list` and `unreadCount` so
 * the bell badge and inbox stay coherent.
 */
function useInvalidateNotifications() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.communications.notifications.list.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: ['inbox-notifications'],
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.communications.notifications.unreadCount.key(),
    });
  };
}

export function useMarkNotificationAsRead() {
  const invalidate = useInvalidateNotifications();
  return useMutation(
    orpc.communications.notifications.markAsRead.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}

export function useMarkAllNotificationsAsRead() {
  const invalidate = useInvalidateNotifications();
  return useMutation(
    orpc.communications.notifications.markAllAsRead.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}

export function useArchiveNotification() {
  const invalidate = useInvalidateNotifications();
  return useMutation(
    orpc.communications.notifications.archive.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}

export function useUnarchiveNotification() {
  const invalidate = useInvalidateNotifications();
  return useMutation(
    orpc.communications.notifications.unarchive.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}

/**
 * One-off admin "Notify…" send about an order / discount group. Invalidates
 * the inbox so a self-addressed notification shows up immediately.
 */
export function useSendAdHocNotification() {
  const invalidate = useInvalidateNotifications();
  return useMutation(
    orpc.communications.notifications.sendAdHoc.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}
